import React, { useState, useEffect, useCallback } from 'react';
import {
  useDataEngine,
  useDataMutation
} from '@dhis2/app-runtime';
import {
  CircularLoader,
  AlertBar,
  Button,
  Card,
  Chip,
  Modal,
  ModalTitle,
  ModalContent,
  NoticeBox,
  ModalActions
} from '@dhis2/ui';
import { IconEdit16, IconDelete16, IconUser16, IconShare16, IconUpload16 } from '@dhis2/ui-icons';
import { positionApi, extractAttributes } from '../api/positionApi';
import { POSITION_ATTRIBUTES, STAFF_TEI_TYPE, STAFF_ATTRIBUTES } from '../api/constants';
import PositionDialog from './PositionDialog';
import BulkPositionImportDialog from './BulkPositionImportDialog';
import { PositionHistoryService, ProgramAssignmentService } from '../services/PositionHistoryService';
import { useAuth } from '../../auth/AuthContext';


const findLocalRootOrgUnit = (userOus = []) => {
  if (!userOus || userOus.length === 0) return null;
  const splitPaths = userOus
    .map(ou => (ou.path || '').split('/').filter(Boolean))
    .filter(p => p.length > 0);

  if (splitPaths.length === 0) return null;

  let rootId = null;
  for (let idx = 0; ; idx++) {
    const idsAtLevel = splitPaths.map(p => p[idx]).filter(Boolean);
    if (idsAtLevel.length !== splitPaths.length) break;
    const first = idsAtLevel[0];
    if (!idsAtLevel.every(id => id === first)) break;
    rootId = first;
  }
  return rootId;
};

const PositionList = () => {
  const engine = useDataEngine();
  const { user } = useAuth();
  const [positions, setPositions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [userOrgUnit, setUserOrgUnit] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [historyService] = useState(new PositionHistoryService(engine));
  const [staffOrgUnits, setStaffOrgUnits] = useState({});
  const [positionHistory, setPositionHistory] = useState({});
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Start as true to show loading initially
  const [loadAttempts, setLoadAttempts] = useState(0);

  const [departments, setDepartments] = useState([]);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkImportResults, setBulkImportResults] = useState(null);

  // Enhanced debug function
  const debugLog = (emoji, message, data = null) => {
    console.log(`${emoji} ${message}`, data ? data : '');
  };

  // State for properly matched staff
  const [matchedStaffList, setMatchedStaffList] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [notification, setNotification] = useState({
    visible: false,
    type: '',
    title: '',
    details: ''
  });


  const enrollPositionInPerformanceProgram = async (teiId, orgUnitId) => {
    try {
      debugLog('🎯', `Checking if position ${teiId} is already enrolled...`);

      // 🔍 Check existing enrollments
      const { enrollments } = await engine.query({
        enrollments: {
          resource: 'enrollments',
          params: {
            trackedEntityInstance: teiId,
            program: 'yiT00XKrrHP', // Your performance program UID
            fields: 'enrollment'
          }
        }
      });


      if (enrollments.enrollments?.length > 0) {
        debugLog('✅', `Position ${teiId} already enrolled. Skipping.`);
        return;
      }

      // ➕ Only enroll if not already enrolled
      debugLog('📤', `Enrolling position ${teiId} in performance program...`);
      const enrollment = await engine.mutate({
        resource: 'enrollments',
        type: 'create',
        data: {
          trackedEntityInstance: teiId,
          program: 'yiT00XKrrHP',
          orgUnit: orgUnitId
        }
      });
      debugLog('✅', `Position enrolled successfully: ${teiId}`);
      return enrollment;

    } catch (error) {
      debugLog('💥', `Enrollment failed for ${teiId}:`, error.message);
      throw error;
    }
  };

  const fetchStaffTeiOrgUnit = async (teiId) => {
    if (!teiId) return null;

    try {
      const { trackedEntityInstance } = await engine.query({
        trackedEntityInstance: {
          resource: `trackedEntityInstances/${teiId}`,
          params: {
            fields: 'orgUnit'
          }
        }
      });

      return trackedEntityInstance?.orgUnit || null;
    } catch (error) {
      console.error('❌ Failed to fetch staff TEI org unit:', error);
      return null;
    }
  };


  const fetchStaffOrgUnit = async (userId) => {
    if (!userId || staffOrgUnits[userId]) return; // already loaded

    try {
      // Step 1: Get username from DHIS2 user
      const userRes = await engine.query({
        user: {
          resource: `users/${userId}`,
          params: { fields: 'userCredentials[username]' }
        }
      });
      const username = userRes.user?.userCredentials?.username;
      if (!username) return;

      // Step 2: Find Staff TEI by username
      const teiRes = await engine.query({
        teis: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: STAFF_TEI_TYPE,
            filter: `g3RRIfDiQra:eq:${username}`, // g3RRIfDiQra = username attribute
            fields: 'trackedEntityInstance,orgUnit',
            ouMode: 'ALL'
          }
        }
      });

      const tei = teiRes.teis?.trackedEntityInstances?.[0];
      if (!tei || !tei.orgUnit) return;

      // Step 3: Get org unit name
      const ouRes = await engine.query({
        ou: {
          resource: `organisationUnits/${tei.orgUnit}`,
          params: { fields: 'name' }
        }
      });

      const orgUnitName = ouRes.ou?.name || tei.orgUnit;

      // Update cache
      setStaffOrgUnits(prev => ({
        ...prev,
        [userId]: { id: tei.orgUnit, name: orgUnitName }
      }));
    } catch (error) {
      console.warn('⚠️ Failed to fetch staff org unit for user:', userId, error);
    }
  };

  // Ensure staff TEI exists and user has data view access to org unit
  const ensureStaffTeiExists = async (userId, orgUnitId) => {
    if (!userId || !orgUnitId) return;

    try {
      debugLog('🔍', `Checking if staff TEI exists for user ${userId}...`);

      // Get user details
      const userRes = await engine.query({
        user: {
          resource: `users/${userId}`,
          params: { fields: 'userCredentials[username],firstName,surname,organisationUnits[id],dataViewOrganisationUnits[id]' }
        }
      });

      const username = userRes.user?.userCredentials?.username;
      const firstName = userRes.user?.firstName || '';
      const surname = userRes.user?.surname || '';
      const currentOrgUnits = userRes.user?.organisationUnits?.map(ou => ou.id) || [];
      const currentDataViewOrgs = userRes.user?.dataViewOrganisationUnits?.map(ou => ou.id) || [];

      if (!username) {
        console.warn('⚠️ No username found for user:', userId);
        return;
      }

      // Check if user already has data view access to this org unit
      if (!currentDataViewOrgs.includes(orgUnitId)) {
        debugLog('🔗', `Granting data view access to org unit ${orgUnitId} for user ${username}...`);

        // Grant data view access
        const updateRes = await engine.mutate({
          resource: `users/${userId}`,
          type: 'update',
          data: {
            dataViewOrganisationUnits: [...currentDataViewOrgs, { id: orgUnitId }]
          }
        });

        if (updateRes) {
          debugLog('✅', `Data view access granted to org unit ${orgUnitId}`);
        }
      }

      // Check if staff TEI already exists
      const teiRes = await engine.query({
        teis: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: STAFF_TEI_TYPE,
            filter: `g3RRIfDiQra:eq:${username}`,
            fields: 'trackedEntityInstance',
            ouMode: 'ALL'
          }
        }
      });

      if (teiRes.teis?.trackedEntityInstances?.length > 0) {
        debugLog('✅', `Staff TEI already exists for user ${username}`);
        return;
      }

      // Create staff TEI if it doesn't exist
      debugLog('📝', `Creating staff TEI for user ${username}...`);

      // Import the createLinkedTEI function
      const { createLinkedTEI } = await import('../../../utils/api');

      const teiData = {
        username,
        userId,
        firstName,
        secondName: surname,
        orgUnit: orgUnitId,
        department: '', // Will be set later if needed
        departmentName: ''
      };

      const result = await createLinkedTEI(teiData, null); // No auth override needed

      if (result?.response?.importSummaries?.[0]?.status === 'SUCCESS') {
        debugLog('✅', `Staff TEI created successfully for user ${username}`);
      } else {
        console.warn('⚠️ Failed to create staff TEI:', result);
      }

    } catch (error) {
      console.error('❌ Error ensuring staff TEI exists:', error);
    }
  };


  const handleBulkImportComplete = (results) => {
    // ✅ Add safety check
    if (!results) {
      console.error('Bulk import results are undefined');
      return;
    }

    setBulkImportResults(results);

    // ✅ Add safety check for results.success
    const successCount = results.success || 0;
    const failedCount = results.failed || 0;

    if (successCount > 0) {
      showNotification(
        'success',
        'Bulk Import Complete',
        `Successfully imported ${successCount} positions. ${failedCount > 0 ? `${failedCount} positions failed to import.` : ''}`
      );
      // Refresh the position list
      handleRefresh();
    } else if (failedCount > 0) {
      showNotification(
        'error',
        'Bulk Import Failed',
        `Failed to import ${failedCount} positions. No positions were imported successfully.`
      );
    }
  };

  // FIXED: Helper function to track staff assignment in HISTORY program
  const trackStaffAssignment = async (positionTeiId, newStaffId, previousStaffId, notes = '', orgUnit = null, retries = 3) => {
    try {
      console.log('🎯 DEBUG - trackStaffAssignment called with:', {
        positionTeiId,
        newStaffId,
        previousStaffId,
        orgUnit,
        hasHistoryService: !!historyService
      });

      // Don't track if no change
      if (newStaffId === previousStaffId) {
        console.log('✅ No staff change detected, skipping history tracking');
        return true;
      }

      // Don't track if both are empty/VACANT
      if (!newStaffId && !previousStaffId) {
        console.log('✅ Both staff IDs empty, skipping history tracking');
        return true;
      }

      debugLog('📝', `Tracking staff assignment for position ${positionTeiId}...`);

      // Add delay before history tracking
      if (retries === 3) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }

      const result = await historyService.trackStaffAssignment(
        positionTeiId,
        newStaffId,
        previousStaffId,
        notes,
        null,
        orgUnit
      );
      console.log('✅ DEBUG - trackStaffAssignment SUCCESS:', result);
      debugLog('✅', `Staff assignment tracked in history: ${positionTeiId}`);
      return true;
    } catch (error) {
      // Handle 409 Conflict - event already exists
      if (error.message?.includes('409') || error.message?.includes('Conflict')) {
        console.warn('⚠️ History event already exists for this assignment, skipping');
        return true; // Not a critical error
      }

      if (error.message?.includes('does not exist') && retries > 0) {
        debugLog('🔄', `TEI not ready for history, retrying... (${retries} attempts left)`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return trackStaffAssignment(
          positionTeiId,
          newStaffId,
          previousStaffId,
          notes,
          orgUnit,
          retries - 1
        );
      }
      console.error('❌ DEBUG - trackStaffAssignment FAILED:', error);
      debugLog('❌', `Staff assignment tracking failed:`, error.message);
      throw error;
    }
  };

  const viewPositionHistory = async (position) => {
    setSelectedPosition(position);
    try {
      // Get the current user from your auth context or state
      const currentUser = {
        id: user?.id, // Make sure you have user object available
        username: user?.username,
        organisationUnits: user?.organisationUnits || []
      };

      const history = await historyService.getPositionHistory(
        position.trackedEntityInstance,
        currentUser // Pass the user object
      );
      setPositionHistory(prev => ({
        ...prev,
        [position.trackedEntityInstance]: history
      }));
      setShowHistoryDialog(true);
    } catch (error) {
      console.error('Failed to load position history:', error);
      showNotification('error', 'History Load Failed', 'Could not load position history');
    }
  };

  const HistoryDialog = () => {
    if (!showHistoryDialog || !selectedPosition) return null;

    const attributes = extractAttributes(selectedPosition.attributes);
    const history = positionHistory[selectedPosition.trackedEntityInstance] || [];

    // Create title as string
    const modalTitle = `Position History - ${attributes[POSITION_ATTRIBUTES.POSITION_TITLE]} (${attributes[POSITION_ATTRIBUTES.POSITION_ID]})`;

    return (
      <Modal open large onClose={() => setShowHistoryDialog(false)}>
        <ModalTitle>
          {modalTitle}
        </ModalTitle>
        <ModalContent>
          {history.length === 0 ? (
            <NoticeBox title="No History">
              No history recorded for this position yet.
            </NoticeBox>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              maxHeight: '60vh',
              overflowY: 'auto'
            }}>
              {history.map((record) => (
                <div
                  key={record.id}
                  style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  {/* Your existing history item content */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <strong style={{ fontSize: '14px', color: '#2C6693' }}>
                        {record.effectiveDate || record.date}
                      </strong>
                      <div
                        style={{
                          backgroundColor: record.type === 'ASSIGNMENT' ? '#e8f5e8' : '#fff3cd',
                          color: record.type === 'ASSIGNMENT' ? '#2d5016' : '#856404',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {record.type}
                      </div>
                    </div>
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                      Staff Member:
                    </div>
                    <div style={{ fontSize: '14px', color: '#555' }}>
                      {record.staffId ? getStaffName(record.staffId) : 'Not assigned'}
                    </div>
                  </div>

                  {record.notes && (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                        Notes:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#555',
                        lineHeight: '1.4',
                        backgroundColor: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #e9ecef'
                      }}>
                        {record.notes}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ModalContent>
        <ModalActions>
          <Button onClick={() => setShowHistoryDialog(false)}>Close</Button>
        </ModalActions>
      </Modal>
    );
  };

  const initializeUserData = useCallback(async () => {
    try {
      debugLog('👤', 'Fetching user data...');
      const { me } = await engine.query({
        me: {
          resource: 'me',
          params: { fields: 'organisationUnits[id,name]' }
        }
      });

      if (me.organisationUnits && me.organisationUnits.length > 0) {
        const userOrgUnitData = {
          id: me.organisationUnits[0].id,
          name: me.organisationUnits[0].name
        };

        debugLog('👤', `User org unit set to: ${userOrgUnitData.id}`, userOrgUnitData.name);

        // ✅ CRITICAL FIX: Set state and THEN load positions
        setUserOrgUnit(userOrgUnitData);

        const programService = new ProgramAssignmentService(engine);
        await programService.ensureProgramAssigned(userOrgUnitData.id);

        // Wait for state to be set, then load positions
        setTimeout(() => {
          loadAllPositions(userOrgUnitData.id);
        }, 300);

      } else {
        debugLog('❌', 'No organisation units assigned to user');
        setUserOrgUnit(null);
        setIsLoading(false);
      }
    } catch (error) {
      debugLog('💥', 'Failed to initialize user data:', error);
      setUserOrgUnit(null);
      setIsLoading(false);

      // Retry after 2 seconds
      if (loadAttempts < 3) {
        setTimeout(() => {
          setLoadAttempts(prev => prev + 1);
          initializeUserData();
        }, 2000);
      }
    }
  }, [engine, loadAttempts]);


  useEffect(() => {
    initializeUserData();
  }, [initializeUserData]);

  // Handle window resize for responsiveness
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadPositionsUsingStaffListPattern = async (orgUnitId, attempt = 0) => {
    try {
      debugLog('🔍', `Loading positions using TEI query (attempt ${attempt + 1})...`);

      const positionsResponse = await engine.query({
        positions: {
          resource: 'trackedEntityInstances',
          params: {
            trackedEntityType: 'UGGo0Ebjl0E',
            fields: 'trackedEntityInstance,attributes[attribute,value]',
            ou: orgUnitId, // Use the passed orgUnitId
            ouMode: 'DESCENDANTS',
            pageSize: 1000
          }
        }
      });

      const positions = positionsResponse.positions.trackedEntityInstances || [];
      debugLog('✅', `Found ${positions.length} positions`);

      // Add orgUnit to each position
      positions.forEach(position => {
        position.orgUnit = orgUnitId;
      });

      positions.forEach((position, index) => {
        const attrs = extractAttributes(position.attributes);
        debugLog(`🎯`, `Position ${index + 1}: ${attrs[POSITION_ATTRIBUTES.POSITION_ID]} - ${attrs[POSITION_ATTRIBUTES.POSITION_TITLE]}`);
      });

      return positions;

    } catch (error) {
      debugLog('❌', `Position loading failed (attempt ${attempt + 1}):`, error.message);

      // Retry up to 3 times
      if (attempt < 2) {
        debugLog('🔄', 'Retrying position loading...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        return loadPositionsUsingStaffListPattern(orgUnitId, attempt + 1);
      }

      throw error;
    }
  };


  const loadAllPositions = async (orgUnitId = null) => {

    let isMounted = true;

    // Use passed orgUnitId or fall back to state
    const targetOrgUnitId = orgUnitId || userOrgUnit?.id;

    if (!targetOrgUnitId) {
      debugLog('❌', 'Cannot load positions: No user org unit available');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      debugLog('🔄', 'Loading positions...');

      const allPositions = await loadPositionsUsingStaffListPattern(targetOrgUnitId);

      debugLog('📊', `TOTAL POSITIONS LOADED: ${allPositions.length}`);

      // ✅ Only update state if component is still mounted
      if (isMounted) {
        setPositions(allPositions || []);

        const incumbents = new Set();
        allPositions.forEach(pos => {
          const attrs = extractAttributes(pos.attributes);
          const incumbent = attrs[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
          if (incumbent && incumbent !== 'VACANT') {
            incumbents.add(incumbent);
          }
        });

        incumbents.forEach(userId => {
          fetchStaffOrgUnit(userId);
        });

      }

    } catch (error) {
      console.error('❌ Position loading failed:', error);

      // ✅ Only update state if component is still mounted
      if (isMounted) {
        setPositions([]);
        showNotification('error', 'Load Failed', 'Failed to load positions. Please try refreshing.');
      }
    } finally {
      // ✅ Only update state if component is still mounted
      if (isMounted) {
        setIsLoading(false);
      }
    }

    // ✅ Cleanup function
    return () => {
      isMounted = false;
    };
  };

  const handleRefresh = () => {
    console.log('🔄 Refreshing positions...');

    // ✅ Check if component is still mounted
    if (!userOrgUnit?.id) {
      console.log('⏳ No user org unit available, skipping refresh');
      // If no userOrgUnit in state, re-initialize
      initializeUserData();
      return;
    }

    // ✅ Use a try-catch to handle any errors
    try {
      loadAllPositions(userOrgUnit.id);
    } catch (error) {
      console.error('❌ Error during refresh:', error);
      showNotification('error', 'Refresh Failed', 'Failed to refresh positions');
    }
  };

  // Replace the existing useEffect for staff fetching
  useEffect(() => {
    const fetchAllStaff = async () => {
      if (!userOrgUnit?.id) {
        console.log('⏳ Waiting for user org unit...');
        return;
      }

      try {
        setStaffLoading(true);
        debugLog('🔍', 'Loading hierarchical staff list...');

        // Get current user's org unit hierarchy
        const { me } = await engine.query({
          me: {
            resource: 'me',
            params: { fields: 'organisationUnits[id,name,level,path]' }
          }
        });

        if (!me.organisationUnits?.length) {
          console.error("❌ No org units assigned to current user");
          setStaffError(new Error("No organisation units assigned"));
          return;
        }

        const primaryOrgUnit = me.organisationUnits[0];
        debugLog('📍', `User primary org unit: ${primaryOrgUnit.name} (${primaryOrgUnit.id})`);

        // Fetch ALL active users
        const usersResp = await engine.query({
          users: {
            resource: 'users',
            params: {
              fields: 'id,firstName,surname,email,userCredentials[username],organisationUnits[id,name,level]',
              pageSize: 2000,
              filter: 'userCredentials.disabled:eq:false'
            }
          }
        });

        const allUsers = usersResp.users?.users || [];
        debugLog('👥', `Total active users in system: ${allUsers.length}`);

        // Fetch ALL staff TEIs in the hierarchy
        const teisResp = await engine.query({
          teis: {
            resource: 'trackedEntityInstances',
            params: {
              trackedEntityType: STAFF_TEI_TYPE,
              fields: 'trackedEntityInstance,orgUnit,attributes[attribute,value]',
              ou: primaryOrgUnit.id,
              ouMode: 'DESCENDANTS',
              pageSize: 2000
            }
          }
        });

        const allTeis = teisResp.teis?.trackedEntityInstances || [];
        debugLog('📌', `Staff TEIs found in hierarchy: ${allTeis.length}`);

        // Match users with TEIs
        const matchedStaff = allUsers
          .map((user) => {
            // Find matching TEI by username
            const userTeis = allTeis.filter((tei) => {
              const usernameAttr = tei.attributes?.find(
                (a) => a.attribute === STAFF_ATTRIBUTES.USERNAME
              );
              return usernameAttr?.value === user.userCredentials?.username;
            });

            // Get user's org unit IDs
            const userOrgUnits = user.organisationUnits?.map((ou) => ou.id) || [];

            // Get all org unit IDs in the hierarchy (from TEIs)
            const hierarchyOrgUnitIds = new Set(allTeis.map((tei) => tei.orgUnit));

            // Check if user is in hierarchy (has TEI or belongs to org unit in hierarchy)
            const isInHierarchy =
              userTeis.length > 0 ||
              userOrgUnits.some((ouId) => hierarchyOrgUnitIds.has(ouId));

            return {
              ...user,
              hasTei: userTeis.length > 0,
              tei: userTeis[0] || null,
              isInHierarchy
            };
          })
          .filter((staff) => staff.isInHierarchy); // Keep only staff in hierarchy

        debugLog('✅', `Staff inside hierarchy: ${matchedStaff.length}`);

        // Format for PositionList consumption
        const formattedStaffList = matchedStaff.map((staff) => {
          const firstName = staff.firstName || '';
          const surname = staff.surname || '';
          const username = staff.userCredentials?.username || '';
          const email = staff.email || '';

          // Get deepest org unit as department
          let department = "No Department";
          if (staff.organisationUnits?.length > 0) {
            const deepestOu = [...staff.organisationUnits].sort(
              (a, b) => b.level - a.level
            )[0];
            department = deepestOu.name;
          }

          const displayName = firstName || surname
            ? `${firstName} ${surname}`
            : username || `User ${staff.id?.slice(0, 8)}`;

          return {
            id: staff.id,
            firstName: firstName,
            surname: surname,
            email: email,
            username: username,
            organisationUnits: staff.organisationUnits || [],
            userCredentials: staff.userCredentials || { username: '' },
            department: department,
            fullName: displayName,
            rawStaff: staff // Keep original for debugging
          };
        });

        setMatchedStaffList(formattedStaffList);
        debugLog('📊', `Staff list updated with ${formattedStaffList.length} staff members`);

        // Log sample of staff for debugging
        if (formattedStaffList.length > 0) {
          console.log('📝 Sample staff:', formattedStaffList.slice(0, 3).map(s => ({
            id: s.id,
            name: s.fullName,
            dept: s.department,
            orgUnits: s.organisationUnits.length
          })));
        }

      } catch (error) {
        console.error('❌ Staff fetch error:', error);
        setStaffError(error);
        showNotification('error', 'Staff Load Failed', 'Failed to load staff data');
      } finally {
        setStaffLoading(false);
      }
    };

    fetchAllStaff();
  }, [engine, userOrgUnit?.id]); // Re-run when userOrgUnit changes

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const query = {
          orgUnits: {
            resource: 'organisationUnits',
            params: {
              paging: false,
              fields: 'id,name,level,path,parent[id,name]'
            }
          }
        };
        const result = await engine.query(query);
        const allUnits = result.orgUnits.organisationUnits || [];

        // Get the root org unit (similar to PositionDialog)
        const { me } = await engine.query({
          me: {
            resource: 'me',
            params: { fields: 'organisationUnits[id,name,level,path]' }
          }
        });

        const rootOrgUnit = findLocalRootOrgUnit(me.organisationUnits);

        // Filter to get departments
        const depts = allUnits
          .filter(ou => ou.path && rootOrgUnit && ou.path.includes(rootOrgUnit.id))
          .map(ou => ({
            id: ou.id,
            name: ou.name,
            level: ou.level
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setDepartments(depts);
        console.log('✅ Loaded departments:', depts.length);

        // Check if HUMAN RESOURCES SERVICES exists
        const hrService = depts.find(d => d.name.includes('HUMAN RESOURCES'));
        console.log('HR Services found:', hrService);

      } catch (error) {
        console.error('❌ Error loading departments:', error);
      }
    };

    loadDepartments();
  }, [engine]);

  // Mutations for CRUD operations
  const [createPosition] = useDataMutation(positionApi.createPosition, {
    onComplete: (result) => {
      const importSummary = result.response?.importSummaries?.[0];

      if (importSummary?.status === 'SUCCESS' && importSummary.reference) {
        const newTeiId = importSummary.reference;
        debugLog('✅', 'Position TEI created successfully:', newTeiId);
      } else {
        const errorMsg = importSummary?.description || 'Position creation failed';
        showNotification('error', 'Creation Failed', errorMsg);
      }
    },
    onError: (error) => {
      console.error('Create position error:', error);
      let userMessage = error.message || 'Unknown error occurred';

      if (error.message?.includes('409') || error.message?.includes('Conflict')) {
        userMessage = 'A position with this ID already exists. Please use a unique Position ID.';
      }

      showNotification('error', 'Creation Failed', userMessage);
    }
  });

  const [updatePosition] = useDataMutation(positionApi.updatePosition, {
    onComplete: () => {
      loadAllPositions();
      showNotification('success', 'Position Updated', 'Position updated successfully');
      setOpenDialog(false);
      setEditingPosition(null);
    },
    onError: (error) => {
      console.error('Update position error:', error);
      showNotification('error', 'Update Failed', error.message || 'Unknown error occurred');
    }
  });

  const [deletePosition] = useDataMutation(positionApi.deletePosition, {
    onComplete: (_, variables) => {
      loadAllPositions();
      showNotification('success', 'Position Deleted', 'Position deleted successfully');
    },
    onError: (error) => {
      console.error('Delete position error:', error);
      showNotification('error', 'Deletion Failed', error.message || 'Unknown error occurred');
    }
  });

  const showNotification = (type, title, details) => {
    setNotification({
      visible: true,
      type,
      title,
      details
    });

    setTimeout(() => {
      setNotification(prev => ({ ...prev, visible: false }));
    }, 8000);
  };


  const handleSavePosition = async (formData) => {
    if (isSubmitting) {
      debugLog('⏳', 'Save already in progress, skipping duplicate...');
      return;
    }

    setIsSubmitting(true);

    if (!userOrgUnit) {
      showNotification('error', 'Save Failed', 'No organisation unit assigned to user');
      setIsSubmitting(false);
      return;
    }

    // Validation
    if (!formData.positionTitle?.trim()) {
      showNotification('error', 'Save Failed', 'Position Title is required');
      setIsSubmitting(false);
      return;
    }

    // ✅ SIMPLIFIED: Determine orgUnit for the position
    let positionOrgUnit = null;

    // Priority 1: Use staff's org unit if staff is selected
    if (formData.currentIncumbent && formData.currentIncumbent !== 'VACANT') {
      const selectedStaff = matchedStaffList.find(s => s.id === formData.currentIncumbent);
      if (selectedStaff?.organisationUnits?.length > 0) {
        // Use the staff's deepest org unit
        const staffOrgUnit = selectedStaff.organisationUnits[0].id;
        positionOrgUnit = staffOrgUnit;
        debugLog('✅', 'Using staff org unit:', positionOrgUnit);
      }
    }

    // Priority 2: Use user's org unit as fallback
    if (!positionOrgUnit && userOrgUnit?.id) {
      positionOrgUnit = userOrgUnit.id;
      debugLog('✅', 'Using user org unit as fallback:', positionOrgUnit);
    }

    // Priority 3: Try to get orgUnit from formData (might come from PositionDialog)
    if (!positionOrgUnit && formData.orgUnit) {
      positionOrgUnit = formData.orgUnit;
      debugLog('✅', 'Using orgUnit from formData:', positionOrgUnit);
    }

    // Final check - if still no orgUnit, show error
    if (!positionOrgUnit) {
      showNotification('error', 'Save Failed', 'Could not determine organisation unit for the position. Please try again or contact support.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Ensure staff TEI exists if assigning a user to position
      let staffTeiId = null;
      if (formData.currentIncumbent && formData.currentIncumbent !== 'VACANT') {
        staffTeiId = await ensureStaffTeiExists(formData.currentIncumbent, positionOrgUnit);
      }

      // Ensure program is assigned to the target org unit
      const programService = new ProgramAssignmentService(engine);
      await programService.ensureProgramAssigned(positionOrgUnit);

      if (editingPosition) {
        // 🔁 UPDATE existing position
        const previousAttributes = extractAttributes(editingPosition.attributes);
        const previousIncumbent = previousAttributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
        const newIncumbent = formData.currentIncumbent;

        const departmentsList = formData._departments || [];

        const departmentName = userOrgUnit?.name || '';

        const updatePayload = {
          trackedEntityInstance: editingPosition.trackedEntityInstance,
          orgUnit: positionOrgUnit, // ✅ Use the determined orgUnit
          positionId: formData.positionId,
          positionTitle: formData.positionTitle,
          department: departmentName, // This will be empty or userOrgUnit name
          jobGrade: formData.jobGrade || '',
          status: formData.status || 'Active',
          currentIncumbent: formData.currentIncumbent || '',
          reportingTo: formData.reportingTo || ''
        };

        debugLog('📤', 'UPDATE TEI payload:', updatePayload);
        await updatePosition({
          teiId: editingPosition.trackedEntityInstance,
          positionData: updatePayload
        });

        // Track history if incumbent changed
        if (newIncumbent && newIncumbent !== previousIncumbent) {
          // Only track when assigning a real staff
          await trackStaffAssignment(
            editingPosition.trackedEntityInstance,
            newIncumbent,
            previousIncumbent,
            'Staff assigned to position',
            user
          );
        }
      } else {

        const departmentsList = formData._departments || [];

        const departmentName = formData.department
          ? (departmentsList.find(dep => dep.id === formData.department)?.name || formData.department)
          : '';

        // ➕ CREATE new position
        const createPayload = {
          orgUnit: positionOrgUnit, // ✅ CORRECT org unit
          positionId: formData.positionId,
          positionTitle: formData.positionTitle,
          department: departmentName,
          jobGrade: formData.jobGrade || '',
          status: formData.status || 'Active',
          currentIncumbent: formData.currentIncumbent || '',
          reportingTo: formData.reportingTo || ''
        };

        debugLog('📤', 'CREATE TEI payload:', createPayload);
        const createResult = await createPosition({ positionData: createPayload });
        const importSummary = createResult?.response?.importSummaries?.[0];
        const newTeiId = importSummary?.reference;

        if (importSummary?.status === 'SUCCESS' && newTeiId) {
          // Enroll in PERFORMANCE MANAGEMENT program
          try {
            await enrollPositionInPerformanceProgram(newTeiId, positionOrgUnit);
          } catch (enrollmentError) {
            console.warn('⚠️ Performance program enrollment failed:', enrollmentError);
          }

          // Track initial staff assignment
          if (formData.currentIncumbent) {
            await trackStaffAssignment(
              newTeiId,
              formData.currentIncumbent,
              null,
              'Position created with initial assignment',
              user
            );
          }

          showNotification('success', 'Position Created', 'Position created successfully');
          // Reload after short delay
          setTimeout(() => loadAllPositions(), 2000);
        } else {
          throw new Error(importSummary?.description || 'Position creation failed');
        }
      }

      debugLog('🎉', 'handleSavePosition COMPLETED SUCCESSFULLY');
      setOpenDialog(false);
      setEditingPosition(null);
    } catch (error) {
      debugLog('💥', 'Error saving position:', error);
      showNotification('error', 'Save Failed', error.message || 'Unknown error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkPositionHistory = async (positionTei) => {
    try {
      console.log('🔍 Checking position history for deletion:', positionTei);

      const history = await historyService.getPositionHistory(positionTei);

      console.log('📋 Position history found:', history.length, 'records');

      // Check if position has any history records
      const hasHistory = history && history.length > 0;

      if (hasHistory) {
        console.log('❌ Position cannot be deleted - has history records:', {
          positionTei,
          historyCount: history.length,
          historyTypes: history.map(h => h.type)
        });
      } else {
        console.log('✅ Position can be deleted - no history records');
      }

      return {
        canDelete: !hasHistory,
        historyCount: history.length,
        historyRecords: history
      };

    } catch (error) {
      console.error('❌ Error checking position history:', error);
      // If we can't check history, be conservative and prevent deletion
      return {
        canDelete: false,
        historyCount: 0,
        historyRecords: [],
        error: error.message
      };
    }
  };

  // Add this function to convert job grade ID to label
  const getJobGradeLabel = (gradeValue) => {
    if (!gradeValue) return '';

    // If it's one of the old IDs, convert to readable name
    const oldMappings = {
      'nLSSLch316I': 'CEO',
      'adeqQmxfCLF': 'Head of Department',
      'gtMJNK0QVL7': 'Top Staff',
      'vdVjNaqxblC': 'Senior Staff'
    };

    // Check if it's an old ID
    if (oldMappings[gradeValue]) {
      return oldMappings[gradeValue];
    }

    // Otherwise, return the value as is (free text)
    return gradeValue;
  };


  const handleDelete = async (position) => {
    try {
      // Extract attributes first
      const attributes = extractAttributes(position.attributes);
      const positionId = attributes[POSITION_ATTRIBUTES.POSITION_ID];
      const positionTitle = attributes[POSITION_ATTRIBUTES.POSITION_TITLE];

      // First check if position has history
      const historyCheck = await checkPositionHistory(position.trackedEntityInstance);

      if (!historyCheck.canDelete) {
        // Show detailed error message
        let errorMessage = `Cannot delete "${positionTitle}" (${positionId}).\n\n`;
        errorMessage += `It has ${historyCheck.historyCount} historical record(s):\n`;

        // Show recent history types
        const recentTypes = historyCheck.historyRecords
          .slice(0, 3)
          .map(record => `• ${record.type || 'Unknown'} - ${new Date(record.date).toLocaleDateString()}`)
          .join('\n');

        errorMessage += recentTypes;

        if (historyCheck.historyRecords.length > 3) {
          errorMessage += `\n• ... and ${historyCheck.historyRecords.length - 3} more records`;
        }

        errorMessage += `\n\nPositions with history cannot be deleted to maintain data integrity.`;

        showNotification(
          'error',
          'Deletion Blocked',
          errorMessage
        );
        return;
      }

      // If no history, proceed with deletion confirmation
      if (window.confirm(`Are you sure you want to delete position "${positionTitle}" (${positionId})?`)) {
        debugLog('🗑️', `Deleting position TEI: ${position.trackedEntityInstance}`);

        await deletePosition({
          teiId: position.trackedEntityInstance
        });

        // Create a history event for deletion
        // ✅ REMOVE THIS ENTIRE BLOCK
        // try {
        //   await trackStaffAssignment(
        //     position.trackedEntityInstance,
        //     '',
        //     attributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT],
        //     'Position deleted',
        //     user
        //   );
        // } catch (historyError) {
        //   debugLog('❌', 'Deletion history tracking failed (non-critical):', historyError);
        // }

      }
    } catch (error) {
      debugLog('💥', 'Error deleting position:', error);
      showNotification('error', 'Deletion Failed', error.message || 'Unknown error occurred');
    }
  };

  const getStaffName = (userId) => {
    if (!userId) return 'Unknown Staff';
    const staff = matchedStaffList.find(s => s.id === userId);
    if (staff) {
      const fullName = `${staff.firstName || ''} ${staff.surname || ''}`.trim();
      return fullName || staff.userCredentials?.username || 'Unknown Staff';
    }
    return 'Unknown Staff';
  };

  const getReportingPositionName = (reportingToTei) => {
    const reportingPosition = positions.find(p => p.trackedEntityInstance === reportingToTei);
    if (reportingPosition) {
      const attrs = extractAttributes(reportingPosition.attributes);
      return attrs[POSITION_ATTRIBUTES.POSITION_TITLE] || 'Unknown Position';
    }
    return 'Unknown Position';
  };

  const getStatusColor = (status) => {
    // Convert status to lowercase for case-insensitive comparison
    const normalizedStatus = (status || '').toLowerCase();

    console.log('🎨 Status Color Debug:', { status, normalizedStatus });

    // In PositionList.jsx, add after loading positions:
    console.log('🔍 DEBUG: Checking what attributes each position has:');
    positions.forEach((position, index) => {
      const attrs = extractAttributes(position.attributes);

    });

    switch (normalizedStatus) {
      case 'active':
        return {
          backgroundColor: '#198504', // Green
          color: '#ffffff'
        };
      case 'vacant':
        return {
          backgroundColor: '#f4d10c', // Yellow
          color: '#000000'
        };
      case 'inactive':
        return {
          backgroundColor: '#f70b0b', // Red
          color: '#ffffff'
        };
      default:
        return {
          backgroundColor: '#666666', // Gray for unknown
          color: '#ffffff'
        };
    }
  };

  // Filter positions based on search term
  const filteredPositions = positions.filter(position => {
    const attributes = extractAttributes(position.attributes);
    const positionId = attributes[POSITION_ATTRIBUTES.POSITION_ID] || '';
    const positionTitle = attributes[POSITION_ATTRIBUTES.POSITION_TITLE] || '';

    // Get incumbent
    const currentIncumbent = attributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];

    // Prefer real staff org unit if available
    let department = '';
    if (currentIncumbent && currentIncumbent !== 'VACANT') {
      const staffOu = staffOrgUnits[currentIncumbent];
      department = staffOu ? staffOu.name : 'Loading...';
      // Optionally trigger fetch if not loaded yet
      if (!staffOu) {
        fetchStaffOrgUnit(currentIncumbent);
      }
    }

    // Fallback to DEPARTMENT attribute if no incumbent or not loaded
    if (!department || department === 'Loading...') {
      department = attributes[POSITION_ATTRIBUTES.DEPARTMENT] || '—';
    }

    const searchLower = searchTerm.toLowerCase();
    return positionId.toLowerCase().includes(searchLower) ||
      positionTitle.toLowerCase().includes(searchLower) ||
      department.toLowerCase().includes(searchLower);
  });

  // Show loading if no org unit yet or still loading
  if (!userOrgUnit || isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularLoader />
        <span style={{ marginLeft: '10px' }}>Loading position data...</span>
      </div>
    );
  }

  // Show error if any
  if (staffError) {
    return (
      <AlertBar error title="Error loading data">
        <div>
          <p><strong>Staff Error:</strong> {staffError?.message || 'None'}</p>
          <p><strong>Current Org Unit:</strong> {userOrgUnit?.name}</p>
          <p><strong>Matched Staff Count:</strong> {matchedStaffList.length}</p>
          <Button onClick={loadAllPositions}>Retry Loading Positions</Button>
        </div>
      </AlertBar>
    );
  }

  return (
    <div>
      {/* Notification */}
      {notification.visible && (
        <div style={{ position: 'fixed', bottom: '20px', right: '400px', zIndex: 10000, width: '350px' }}>
          <AlertBar
            {...(notification.type === 'success' ? { success: true } :
              notification.type === 'warning' ? { warning: true } :
                { error: true })}
            title={notification.title}
            onHidden={() => setNotification({ visible: false })}
          >
            {notification.details}
          </AlertBar>
        </div>
      )}

      {/* Bulk Import Results Summary */}
      {bulkImportResults && (
        <div style={{
          marginBottom: '1rem',
          padding: '12px',
          backgroundColor: (bulkImportResults.failed || 0) > 0 ? '#fff3cd' : '#e8f5e8',
          border: `1px solid ${(bulkImportResults.failed || 0) > 0 ? '#ffeaa7' : '#c3e6cb'}`,
          borderRadius: '4px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>
                {(bulkImportResults.failed || 0) === 0 ? '✅ ' : '⚠️ '}
                Bulk Import Results:
              </strong>
              <span style={{ marginLeft: '8px' }}>
                {bulkImportResults.success || 0} successful, {bulkImportResults.failed || 0} failed
              </span>
            </div>
            <Button
              small
              onClick={() => setBulkImportResults(null)}
              style={{ padding: '2px 8px', fontSize: '12px' }}
            >
              Dismiss
            </Button>
          </div>
          {/* ✅ SAFE CHECK: Add optional chaining and default value */}
          {(bulkImportResults.errors?.length || 0) > 0 && (
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              <details>
                <summary>View errors ({bulkImportResults.errors?.length || 0})</summary>
                <div style={{
                  marginTop: '8px',
                  maxHeight: '150px',
                  overflow: 'auto',
                  backgroundColor: 'white',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #dee2e6'
                }}>
                  {/* ✅ SAFE CHECK: Use optional chaining and default to empty array */}
                  {(bulkImportResults.errors || []).slice(0, 10).map((error, index) => (
                    <div key={index} style={{ marginBottom: '4px' }}>
                      <strong>Row {error?.row || 'Unknown'}:</strong> {error?.error || 'Unknown error'}
                    </div>
                  ))}
                  {(bulkImportResults.errors?.length || 0) > 10 && (
                    <div>... and {(bulkImportResults.errors?.length || 0) - 10} more errors</div>
                  )}
                </div>
              </details>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? '1rem' : '0'
      }}>
        <h4 style={{ margin: 0, color: '#2C6693' }}>
          Position Management {userOrgUnit && `- ${userOrgUnit.name}`}
        </h4>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Search Input */}
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search positions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '8px 12px 8px 35px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                width: '250px'
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666'
            }}>
              🔍
            </span>
          </div>
          {/* Refresh Button */}
          <Button
            primary
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </Button>
          {/* Bulk Import Button - NEW */}
          <Button
            primary
            icon={<IconUpload16 />}
            onClick={() => setShowBulkImport(true)}
          >
            Bulk Import
          </Button>
          {/* Add New Position Button */}
          <Button
            primary
            icon={<IconUser16 />}
            onClick={() => {
              setEditingPosition(null);
              setOpenDialog(true);
            }}
          >
            Add New Position
          </Button>
        </div>
      </div>

      {/* Debug Info */}
      <div style={{ marginBottom: '1rem', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
        <small>
          Positions: {positions.length} | Staff: {matchedStaffList.length}
        </small>
      </div>

      {/* Positions Grid */}
      {positions.length === 0 && !isLoading ? (
        <Card>
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            border: '2px dashed #e0e0e0',
            borderRadius: '4px',
          }}>
            <IconShare16 style={{ fontSize: '48px', color: '#6C757D', marginBottom: '1rem' }} />
            <h3 style={{ color: '#6C757D', marginBottom: '0.5rem' }}>
              No Positions Found
            </h3>
            <p style={{ color: '#6C757D', margin: 0 }}>
              Get started by creating your first position.
            </p>
            <Button
              primary
              style={{ marginTop: '1rem' }}
              onClick={() => {
                setEditingPosition(null);
                setOpenDialog(true);
              }}
            >
              Create First Position
            </Button>
          </div>
        </Card>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '1rem',
          backgroundColor: '#f6f8f9ff',
          padding: '10px',
          borderRadius: '8px'
        }}>
          {filteredPositions && filteredPositions.map((position) => {
            const attributes = extractAttributes(position.attributes);
            const positionId = attributes[POSITION_ATTRIBUTES.POSITION_ID];
            const positionTitle = attributes[POSITION_ATTRIBUTES.POSITION_TITLE];
            const department = attributes[POSITION_ATTRIBUTES.DEPARTMENT];
            const jobGrade = attributes[POSITION_ATTRIBUTES.JOB_GRADE];
            const currentIncumbent = attributes[POSITION_ATTRIBUTES.CURRENT_INCUMBENT];
            const status = attributes[POSITION_ATTRIBUTES.POSITION_STATUS] || 'Active';

            return (
              <Card key={position.trackedEntityInstance}
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  border: '1px solid #e0e0e0',
                }}>
                <div style={{ padding: '1.5rem' }}>
                  {/* Header with Position ID and Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '1rem',
                  }}>
                    <strong style={{
                      color: '#2C6693',
                      fontSize: '1.1rem',
                      fontWeight: 'bold'
                    }}>
                      Position ID: {positionId}
                    </strong>
                  </div>

                  {/* Position Title */}
                  <h4 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1rem',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {positionTitle}
                  </h4>

                  {/* Department and Grade */}
                  <div style={{ marginBottom: '1rem' }}>
                    
                    {getJobGradeLabel(jobGrade) && (
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                        <strong>Grade:</strong> {getJobGradeLabel(jobGrade)}
                      </p>
                    )}
                  </div>

                  {/* Current Incumbent */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 'bold' }}>
                      Current Incumbent:
                    </p>
                    {currentIncumbent ? (
                      <Chip>{getStaffName(currentIncumbent)}</Chip>
                    ) : (
                      <Chip warning>Vacant</Chip>
                    )}
                    <div
                      style={{
                        backgroundColor: getStatusColor(status).backgroundColor,
                        color: getStatusColor(status).color,
                        fontWeight: 'bold',
                        border: 'none',
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        minWidth: '70px',
                        textAlign: 'center',
                        display: 'inline-block'
                      }}
                    >
                      {status}
                    </div>
                  </div>

                  {attributes[POSITION_ATTRIBUTES.REPORTING_TO] ? (
                    <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#666' }}>
                      <strong>Reports to:</strong> {getReportingPositionName(attributes[POSITION_ATTRIBUTES.REPORTING_TO])}
                    </p>
                  ) : (
                    <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>
                      <strong>Reporting to:</strong> Not set
                    </p>
                  )}

                  {/* Footer with Status and Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderTop: '1px solid #f0f0f0',
                    paddingTop: '1rem',
                  }}>

                    <button
                      style={{
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: '#6c757d',
                        borderRadius: '8px',
                        border: 'none',
                        width: '80px',
                        height: '30px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        fontSize: '12px'
                      }}
                      onClick={() => viewPositionHistory(position)}
                    >
                      📊 History
                    </button>
                    <button
                      style={{
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: '#083ea2ff',
                        borderRadius: '8px',
                        border: 'none',
                        width: '80px',
                        height: '30px',
                        cursor: 'pointer'
                      }}
                      onClick={() => { setEditingPosition(position); setOpenDialog(true); }}
                    >
                      <IconEdit16 />Edit
                    </button>

                    {/* Enhanced Delete Button with History Check */}
                    <button
                      style={{
                        boxShadow: "0 8px 10px rgba(108, 105, 105, 0.5)",
                        color: 'white',
                        backgroundColor: position.hasHistory ? '#6c757d' : '#e60d09ff', // Gray if has history, red if deletable
                        borderRadius: '8px',
                        border: 'none',
                        width: '80px',
                        height: '30px',
                        cursor: position.hasHistory ? 'not-allowed' : 'pointer',
                        opacity: position.hasHistory ? 0.6 : 1
                      }}
                      onClick={() => handleDelete(position)}
                      disabled={position.hasHistory}
                      title={position.hasHistory ?
                        "Cannot delete - position has historical records" :
                        "Delete position"}
                    >
                      <IconDelete16 />
                      Delete
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Position Dialog */}
      {openDialog && (
        <PositionDialog
          open={openDialog}
          position={editingPosition}
          staffList={matchedStaffList}
          positionsList={positions} // Add this line
          //departments={departments} 
          onSave={handleSavePosition}
          onClose={() => {
            setOpenDialog(false);
            setEditingPosition(null);
          }}
          engine={engine}
        />
      )}

      {/* Bulk Import Dialog - NEW */}
      {showBulkImport && (
        <BulkPositionImportDialog
          open={showBulkImport}
          onClose={() => {
            setShowBulkImport(false);
            setBulkImportResults(null);
          }}
          onImportComplete={handleBulkImportComplete}
          onComplete={() => {
            setShowBulkImport(false);
            // Refresh positions after bulk import
            handleRefresh();
            showNotification('success', 'Bulk Import Complete', 'Positions imported successfully');
          }}
          staffList={matchedStaffList}
          positionsList={positions}
          userOrgUnit={userOrgUnit} // Pass the user's org unit
        />
      )}

      {/* History Dialog */}
      <HistoryDialog />
    </div>
  );
};

export default PositionList;